import React from "react";
// @ts-ignore - @lingui/macro types compatibility
import { Trans } from "@lingui/react/macro";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import { useState } from "react";

import { fetchCachedTTSAudio } from "./speakerService";
import { getSelectedVoice } from "./speakerVoices";

interface SpeakerButtonProps {
  text: string;
  language: string;
  classes?: {
    icons?: string;
  };
}

interface LangMap {
  [key: string]: string;
}

const SpeakerButton: React.FC<SpeakerButtonProps> = ({
  text,
  language,
  classes = {},
}) => {
  const [isSpeaking, setIsSpeaking] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSpeak = async () => {
    // Try GiellaLT TTS API first (with caching)
    setIsLoading(true);
    const selectedVoice = getSelectedVoice(language);
    const audioUrl = await fetchCachedTTSAudio(text, language, selectedVoice);
    setIsLoading(false);

    if (audioUrl) {
      // Play the MP3 from the API
      const audio = new Audio(audioUrl);

      audio.onplay = () => {
        setIsSpeaking(true);
      };

      audio.onended = () => {
        setIsSpeaking(false);
      };

      audio.onerror = () => {
        setIsSpeaking(false);
        console.error("Error playing audio");
      };

      audio.play();
      return;
    }

    // Fallback to browser speech synthesis for other languages
    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(text);

      const langMap: LangMap = {
        fin: "fi-FI",
        nob: "nb-NO",
        swe: "sv-SE",
        eng: "en-US",
      };

      utterance.lang = langMap[language] || "en-US";
      utterance.rate = 0.9;

      utterance.onstart = () => {
        setIsSpeaking(true);
      };

      utterance.onend = () => {
        setIsSpeaking(false);
      };

      utterance.onerror = () => {
        setIsSpeaking(false);
      };

      window.speechSynthesis.speak(utterance);
    }
  };

  const isDisabled = isSpeaking || isLoading;

  return (
    <Tooltip
      title={
        isLoading ? (
          <Trans>Loading pronunciation...</Trans>
        ) : (
          <Trans>Listen to pronunciation</Trans>
        )
      }
    >
      <span>
        <IconButton
          className={classes?.icons}
          onClick={handleSpeak}
          disabled={isDisabled}
          size="small"
          aria-label="speak text"
          sx={
            isLoading
              ? {
                  "@keyframes throb": {
                    "0%, 100%": { opacity: 0.3 },
                    "50%": { opacity: 1 },
                  },
                  animation: "throb 1.5s ease-in-out infinite",
                }
              : undefined
          }
        >
          <VolumeUpIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default SpeakerButton;
